<template>
  <div class="flex flex-col gap-16">
    <BittsInput
      v-if="formConfig.isCustomPopulation"
      v-model="populationName"
      placeholder="Enter name"
      form-label="Population Name"
      name="population-name"
      :status="populationNameError ? 'danger' : 'default'"
      :danger-text="populationNameError"
      :disabled="formConfig.isViewMode"
    />
    <BittsSelect
      v-if="formConfig.isCustomPopulation"
      v-model="selectedPopType"
      placeholder="Select"
      :options="SELECTED_POP_TYPE_OPTIONS"
      :disabled="formConfig.isViewMode"
      :form-label="{
        title: 'Population Type',
        helpText: populationTypeHelpText,
      }"
    />
    <div v-if="formConfig.isOpenData" class="flex flex-col gap-4">
      <span class="text-neutral-text-strong text-sm font-bold">
        Data Source
      </span>
      <div
        class="flex items-center gap-8 rounded-bts-sm border border-neutral-border bg-neutral-background-disabled px-12 py-8"
      >
        <FontAwesomeIcon
          :icon="['fak', 'managed-offline-partner']"
          class="text-info-accent text-16"
        />
        <span class="text-neutral-text text-sm">Open Data</span>
      </div>
    </div>
    <BittsSelect
      v-else-if="canPickDataSource"
      v-model="selectedFeedId"
      form-label="Data Source"
      placeholder="Select"
      :disabled="
        formConfig.isEdit || formConfig.isSourceLocked || formConfig.isViewMode
      "
      :options="dataSourceOptions"
      option-type="svg"
      @update:model-value="onFeedChange"
    />

    <BittsSelect
      v-if="!formConfig.isOpenData"
      v-model="selectedObject"
      :options="sourceFieldOptions"
      :form-label="{
        title: objectLabel,
        helpText: helpText,
      }"
      :disabled="
        !selectedFeedId ||
        formConfig.isEdit ||
        formConfig.isSourceLocked ||
        formConfig.isViewMode
      "
    />
    <BittsTextArea
      v-model="populationDescription"
      form-label="Description (optional)"
      name="population-description"
      placeholder="Enter description"
      :disabled="formConfig.isViewMode"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsInput, BittsSelect, BittsTextArea } from '@crossbeam/bitts';

import { computed, toRefs, watch } from 'vue';

import useOfflinePartner from '@/composables/useOfflinePartner';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
  isGoogleOrCsvSource,
} from '@/constants/data_sources';
import { usePopulationsStore } from '@/stores';
import { Feed } from '@/types/feeds';
import {
  CUSTOMERS_STANDARD_TYPE,
  OPEN_OPPS_STANDARD_TYPE,
  OTHER,
  PROSPECTS_STANDARD_TYPE,
  type PopulationDataSourceConfig,
  type PopulationFormConfig,
  StandardPopulationType,
} from '@/types/populations';
import {
  buildDataSourceOptions,
  buildSourceFieldOptions,
} from '@/utils/populationOptions';

const props = defineProps<Props>();

const emit = defineEmits<{
  'feed-change': [feedId: number | 'add-new-source'];
}>();

const SELECTED_POP_TYPE_OPTIONS = [
  { label: 'Customers', value: CUSTOMERS_STANDARD_TYPE },
  { label: 'Open Opportunities', value: OPEN_OPPS_STANDARD_TYPE },
  { label: 'Prospects', value: PROSPECTS_STANDARD_TYPE },
  { label: 'Other', value: OTHER },
] as const;

const populationTypeHelpText =
  'Choose the category that best matches your goal ' +
  '— use Customers to define your customer base, Open Opportunities ' +
  'to work or win deals, Prospects to generate new opportunities, and Other for segments ' +
  "that don't fit these categories.";

type Props = {
  formConfig: PopulationFormConfig;
  dataSourceConfig: PopulationDataSourceConfig;
  originalPopulationName?: string;
  selectedFeed?: Feed | null;
};

const populationName = defineModel<string | undefined>('populationName');
const populationDescription = defineModel<string | undefined>(
  'populationDescription',
);
const selectedFeedId = defineModel<number | null | 'add-new-source'>(
  'selectedFeedId',
);
const selectedObject = defineModel<number | null>('selectedObject');
const selectedPopType = defineModel<
  StandardPopulationType | typeof OTHER | null
>('selectedPopType');
const isPopulationNameValid = defineModel<boolean>('isPopulationNameValid');

const { formConfig, dataSourceConfig, originalPopulationName, selectedFeed } =
  toRefs(props);

const populationsStore = usePopulationsStore();
const { offlinePopulations } = useOfflinePartner();

const dataSourceOptions = computed(() =>
  buildDataSourceOptions(
    dataSourceConfig.value.currentFeeds,
    !formConfig.value.isOfflinePartner,
  ),
);

const canPickDataSource = computed(
  () =>
    !formConfig.value.isOfflinePartner || dataSourceOptions.value.length > 1,
);

const isCSVSelected = computed(() => {
  if (!selectedFeedId.value || typeof selectedFeedId.value !== 'number')
    return false;
  return selectedFeed.value?.integration?.type === FILE_UPLOAD_DATA_SOURCE_TYPE;
});

const sourceFieldOptions = computed(() =>
  buildSourceFieldOptions(
    dataSourceConfig.value.currentSources,
    typeof selectedFeedId.value === 'number' ? selectedFeedId.value : null,
    selectedFeed.value ?? null,
    isCSVSelected.value,
    dataSourceConfig.value.currentUploadNames,
  ),
);

const isCsvOrSheetsSource = computed(() => {
  if (!selectedFeed.value) return false;
  return isGoogleOrCsvSource(selectedFeed.value.integration?.type || '');
});

const objectLabel = computed(() =>
  isCsvOrSheetsSource.value ? 'Select file' : 'Object',
);

const helpText = computed(() =>
  isCsvOrSheetsSource.value
    ? 'Select the file this population will be built from'
    : 'Select the CRM object this population will be built from',
);

const availablePopulations = computed(() => {
  return formConfig.value.isOfflinePartner
    ? offlinePopulations.value
    : populationsStore.populations;
});

watch(
  [populationName, () => formConfig.value.isCustomPopulation],
  () => {
    if (!populationName.value || !formConfig.value.isCustomPopulation) {
      isPopulationNameValid.value = true;
      return;
    }
    const trimmedName = populationName.value.trim();
    const reservedNames = ['Customers', 'Prospects', 'Open Opportunities'];
    const isReservedName = reservedNames.some(
      (name) => trimmedName?.toLowerCase() === name.toLowerCase(),
    );
    const isDuplicateName = (() => {
      if (formConfig.value.isEdit) {
        const hasNameChanged =
          originalPopulationName.value &&
          trimmedName.toLowerCase() !==
            originalPopulationName.value.toLowerCase();
        if (!hasNameChanged) return false;
      }

      return availablePopulations.value.some(
        (population) =>
          population.name.toLowerCase() === trimmedName.toLowerCase(),
      );
    })();

    isPopulationNameValid.value = !isReservedName && !isDuplicateName;
  },
  { immediate: true },
);

const populationNameError = computed(() => {
  if (isPopulationNameValid.value) return undefined;

  if (!populationName.value || !formConfig.value.isCustomPopulation)
    return undefined;

  const trimmedName = populationName.value.trim();
  const reservedNames = ['Customers', 'Prospects', 'Open Opportunities'];
  const isReservedName = reservedNames.some(
    (name) => trimmedName?.toLowerCase() === name.toLowerCase(),
  );

  if (isReservedName) {
    return 'This name is reserved for standard populations.';
  }

  const isDuplicateName = (() => {
    if (formConfig.value.isEdit) {
      const hasNameChanged =
        originalPopulationName.value &&
        trimmedName.toLowerCase() !==
          originalPopulationName.value.toLowerCase();
      if (!hasNameChanged) return false;
    }

    return availablePopulations.value.some(
      (population) =>
        population.name.toLowerCase() === trimmedName.toLowerCase(),
    );
  })();

  if (isDuplicateName) {
    return 'A population with this name already exists.';
  }

  return undefined;
});

function onFeedChange(feedId: number | 'add-new-source') {
  selectedObject.value = null;
  emit('feed-change', feedId);
}

watch(populationName, (newValue) => {
  if (!formConfig.value.isCustomPopulation) {
    return;
  }

  if (!newValue) {
    selectedPopType.value = null;
    return;
  }

  const input = newValue.toLowerCase();

  const containsAny = (terms: string[]): boolean => {
    return terms.some((term) => input.includes(term));
  };

  const otherTerms = [
    'former',
    'churn',
    'non',
    'past',
    'lost',
    'ex-customer',
    'ex-client',
    'ex-opp',
  ];
  if (containsAny(otherTerms)) {
    selectedPopType.value = OTHER;
    return;
  }

  const customerTerms = [
    'customer',
    'client',
    'won opportunities',
    'won opps',
    'won opp',
  ];
  const wonOpportunityTerms = ['won opportunities', 'won opps', 'won opp'];
  const customerExclusionTerms = [
    'prospect',
    'opportunity',
    'opp',
    'opps',
    'target',
  ];

  if (containsAny(customerTerms)) {
    const hasWonOpportunityTerms = containsAny(wonOpportunityTerms);
    const hasExclusionTerms = containsAny(customerExclusionTerms);
    if (hasWonOpportunityTerms || !hasExclusionTerms) {
      selectedPopType.value = CUSTOMERS_STANDARD_TYPE as StandardPopulationType;
      return;
    }
  }

  const prospectTerms = [
    'prospect',
    'lead',
    'target',
    'tam',
    'tal',
    'forecast',
    'marketing',
    'mql',
    'icp',
    'wishlist',
    'high intent',
    'key account',
    'named account',
    'stategic account',
    'priority account',
  ];

  if (containsAny(prospectTerms)) {
    selectedPopType.value = PROSPECTS_STANDARD_TYPE as StandardPopulationType;
    return;
  }

  const opportunityTerms = ['opportunit', 'opp', 'opps', 'pipeline'];
  const opportunityExclusionTerms = ['closed', 'lost', 'disqualified', 'won'];

  if (
    containsAny(opportunityTerms) &&
    !containsAny(opportunityExclusionTerms)
  ) {
    selectedPopType.value = OPEN_OPPS_STANDARD_TYPE as StandardPopulationType;
  }
});

watch(
  [dataSourceOptions, () => formConfig.value.isEdit],
  () => {
    if (formConfig.value.isEdit || selectedFeedId.value) return;

    const actualFeeds = dataSourceOptions.value.filter(
      (option) => option.value !== 'add-new-source',
    );

    if (actualFeeds.length === 1 && actualFeeds[0]) {
      selectedFeedId.value = actualFeeds[0].value as number;
      onFeedChange(actualFeeds[0].value as number);
    }
  },
  { immediate: true },
);

watch(
  [sourceFieldOptions, selectedFeedId, () => formConfig.value.isEdit],
  () => {
    if (
      formConfig.value.isEdit ||
      !selectedFeedId.value ||
      selectedObject.value
    )
      return;

    const isSalesforce =
      selectedFeed.value?.integration?.type === SALESFORCE_DATA_SOURCE_TYPE;
    if (isSalesforce) {
      const accountOption = sourceFieldOptions.value.find(
        (option) => option.name === 'Account',
      );
      if (accountOption?.value !== undefined) {
        selectedObject.value = accountOption.value as number;
        return;
      }
    }

    if (sourceFieldOptions.value.length === 1) {
      const singleOption = sourceFieldOptions.value[0];
      if (singleOption?.value !== undefined) {
        selectedObject.value = singleOption.value as number;
      }
    }
  },
  { immediate: true },
);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

/* Disabled state overrides for Bitts components */
:deep(.c-bitts-input__input.disabled) {
  @apply bg-neutral-background-weak! opacity-100! outline-0!;
}

:deep(.c-bitts-input__input.disabled .ant-input) {
  @apply text-neutral-text-placeholder!;
}

:deep(
    .ant-select-disabled.ant-select:not(.ant-select-customize-input)
      .ant-select-selector
  ) {
  @apply bg-neutral-background-weak text-neutral-text-placeholder opacity-100!;
}

:deep(.ant-select-disabled .ant-select-selection-item),
:deep(.ant-select-disabled .ant-select-selection-placeholder) {
  @apply text-neutral-text-placeholder opacity-100!;
}

:deep(textarea.c-bitts-textarea.ant-input.disabled) {
  @apply bg-neutral-background-weak! opacity-100! outline-none! text-neutral-text-placeholder! border-neutral-border!;
}

:deep(textarea.c-bitts-textarea.ant-input.disabled)::placeholder {
  @apply text-neutral-text-placeholder! opacity-100!;
}
</style>
